import React, { useState } from 'react';
import { CheckCircle2, Check, AlertTriangle, AlertCircle, Zap, Sparkles } from 'lucide-react';
import { Issue, Severity } from '../types';
import { t } from '../services/i18n';
import { useLanguageUpdate } from '../src/hooks/useLanguageChange';

interface SummaryCardsProps {
  issues: Issue[];
}

export const SummaryCards: React.FC<SummaryCardsProps> = ({ issues }) => {
  // Listen for language changes to update translations
  useLanguageUpdate();
  // State for expand/collapse
  const [expandedWins, setExpandedWins] = useState(false);
  const [expandedPriority, setExpandedPriority] = useState(false);
  const [expandedQuickWins, setExpandedQuickWins] = useState(false);

  // Categorize issues
  const goodIssues = issues.filter(i => i.severity === Severity.INFO || i.severity === Severity.LOW);
  const criticalIssues = issues.filter(i => i.severity === Severity.CRITICAL || i.severity === Severity.HIGH);
  const quickWins = issues.filter(i => 
    (i.severity === Severity.MEDIUM || i.severity === Severity.LOW) && 
    i.fix && i.fix.length < 200 // Short fixes = quick wins
  );

  // Get good things working
  const goodThings = [
    t('results.httpsEnabled') || 'HTTPS enabled',
    t('results.mobileResponsive') || 'Mobile responsive',
    t('results.structuredDataPresent') || 'Structured data present',
    t('results.fastPageLoad') || 'Fast page load',
    t('results.noBrokenLinks') || 'No broken links'
  ].filter((_, idx) => idx < goodIssues.length || idx < 3);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-12 px-6 relative z-20 mb-12">
      {/* 🎉 What's Working Well */}
      <div 
        className="bg-slate-800 rounded-xl shadow-lg border border-emerald-500/30 p-5 transform transition-all hover:scale-105 hover:border-emerald-500/50"
        style={{ animation: 'slideUp 0.5s ease-out 0.1s both' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-emerald-500/20 rounded-lg">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">{t('results.wins') || 'Wins'}</h3>
            <p className="text-xs text-emerald-400 font-semibold">
              {goodIssues.length > 0 ? t('results.winsDesc', { count: String(goodIssues.length) }) || `${goodIssues.length} things working well` : (t('results.goodFundamentals') || 'Good fundamentals')}
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {(expandedWins ? goodThings : goodThings.slice(0, 3)).map((item, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span className="text-slate-300">{item}</span>
            </div>
          ))}
          {goodThings.length > 3 && (
            <button 
              onClick={() => setExpandedWins(!expandedWins)}
              className="text-emerald-400 text-xs font-semibold hover:underline"
            >
              {expandedWins ? (t('results.showLess') || 'Show less') : `+${goodThings.length - 3} ${t('results.more') || 'more'}`}
            </button>
          )}
        </div>
      </div>

      {/* ⚠️ Needs Attention */}
      <div 
        className="bg-slate-800 rounded-xl shadow-lg border border-orange-500/30 p-5 transform transition-all hover:scale-105 hover:border-orange-500/50"
        style={{ animation: 'slideUp 0.5s ease-out 0.2s both' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-orange-500/20 rounded-lg animate-pulse">
            <AlertTriangle className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">{t('results.priority') || 'Priority'}</h3>
            <p className="text-xs text-orange-400 font-semibold">
              {t('results.priorityDesc', { count: String(criticalIssues.length) }) || `${criticalIssues.length} ${criticalIssues.length === 1 ? 'issue' : 'issues'} to fix first`}
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {(expandedPriority ? criticalIssues : criticalIssues.slice(0, 3)).map((issue, idx) => (
            <div key={issue.id || idx} className="flex items-start gap-2 text-sm">
              <AlertCircle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
              <span className="text-slate-300 line-clamp-2">{issue.title}</span>
            </div>
          ))}
          {criticalIssues.length > 3 && (
            <button 
              onClick={() => setExpandedPriority(!expandedPriority)}
              className="text-orange-400 text-xs font-semibold hover:underline"
            >
              {expandedPriority ? (t('results.showLess') || 'Show less') : `+${criticalIssues.length - 3} ${t('results.more') || 'more'}`}
            </button>
          )}
          {criticalIssues.length === 0 && (
            <p className="text-slate-400 text-sm">{t('results.noCriticalIssues') || 'No critical issues! 🎉'}</p>
          )}
        </div>
      </div>

      {/* 🚀 Quick Wins */}
      <div 
        className="bg-slate-800 rounded-xl shadow-lg border border-blue-500/30 p-5 transform transition-all hover:scale-105 hover:border-blue-500/50"
        style={{ animation: 'slideUp 0.5s ease-out 0.3s both' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 bg-blue-500/20 rounded-lg">
            <Zap className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">{t('results.quickWins') || 'Quick Wins'}</h3>
            <p className="text-xs text-blue-400 font-semibold">
              {t('results.quickWinsDesc', { count: String(quickWins.length) }) || `${quickWins.length} ${quickWins.length === 1 ? 'easy improvement' : 'easy improvements'}`}
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {(expandedQuickWins ? quickWins : quickWins.slice(0, 3)).map((win, idx) => (
            <div key={win.id || idx} className="flex items-start gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <span className="text-slate-300 line-clamp-2">{win.title}</span>
            </div>
          ))}
          {quickWins.length > 3 && (
            <button 
              onClick={() => setExpandedQuickWins(!expandedQuickWins)}
              className="text-blue-400 text-xs font-semibold hover:underline"
            >
              {expandedQuickWins ? (t('results.showLess') || 'Show less') : `+${quickWins.length - 3} ${t('results.more') || 'more'}`}
            </button>
          )}
          {quickWins.length === 0 && (
            <p className="text-slate-400 text-sm">{t('results.focusPriority') || 'Focus on priority issues first'}</p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

